import React from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';

import {useReservation} from 'context/reservation';
import {
  ComputedReservationDetailsContextProps,
  useComputedReservationDetails,
} from 'context/computedReservationDetails';
import {useErrorModal, useIsMounted} from 'utils/hooks';
import {getFinishCheckinHistoryArgs} from 'utils/checkin';
import {
  COUNTRY_CODES_WITHOUT_SIGNATURE,
  TIMEOUT_BEFORE_REDIRECT_MS,
} from 'utils/constants';
import {getHousingCountryCode} from 'utils/reservation';
import {useRegisterGuest} from 'hooks/useRegisterGuest';

import {WebcamRefTypes} from 'components/Webcam/Webcam';
import Header from 'components/Header';
import Webcam from 'components/Webcam';
import Button from 'components/Button';
import Modal from 'components/Modal';

import successIcon from 'assets/success-icon.svg';
import cameraIcon from 'assets/camera.svg';
import userIcon from 'assets/user.svg';

import {DimensionsWrapper} from 'styled/common';
import {ButtonIcon, ButtonWrapper, Content, Hint, Title} from './styled';

function getHeaderProps({
  hasGuestMembers,
  hasBiomatchForGuestLeader,
  isContractEnabled,
  shortCustomForm,
  isDubaiReservation,
  isThailandReservation,
  isGreeceReservation,
  reservation,
  hasBiomatchForAllGuests,
}: ComputedReservationDetailsContextProps) {
  const requiredIdOrPassport = shortCustomForm?.required_id_or_passport;
  const countryCode = getHousingCountryCode(reservation);

  if (hasGuestMembers && hasBiomatchForAllGuests) {
    if (isGreeceReservation) {
      if (requiredIdOrPassport) {
        return {
          steps: 3,
          activeStep: 3,
        };
      }
    }

    if (COUNTRY_CODES_WITHOUT_SIGNATURE.includes(countryCode) && !isThailandReservation) {
      if (requiredIdOrPassport) {
        return {
          steps: 3,
          activeStep: 3,
        };
      }
    }

    if (isThailandReservation || isDubaiReservation || requiredIdOrPassport) {
      if (isContractEnabled) {
        return {
          steps: 4,
          activeStep: 3,
        };
      }

      return {
        steps: 3,
        activeStep: 3,
      };
    }

    if (requiredIdOrPassport) {
      return {
        steps: 4,
        activeStep: 3,
      };
    }

    return {
      steps: 3,
      activeStep: 3,
    };
  }

  if (hasGuestMembers) {
    if (isGreeceReservation) {
      if (requiredIdOrPassport) {
        return {
          steps: 2,
          activeStep: 2,
        };
      }
    }

    if (COUNTRY_CODES_WITHOUT_SIGNATURE.includes(countryCode) && !isThailandReservation) {
      if (requiredIdOrPassport) {
        return {
          steps: 2,
          activeStep: 2,
        };
      }
    }

    if (isThailandReservation || isDubaiReservation || requiredIdOrPassport) {
      if (isContractEnabled) {
        return {
          steps: 3,
          activeStep: 2,
        };
      }

      return {
        steps: 2,
        activeStep: 2,
      };
    }

    if (requiredIdOrPassport) {
      return {
        steps: 3,
        activeStep: 2,
      };
    }

    return {
      steps: 2,
      activeStep: 2,
    };
  }

  if (isContractEnabled) {
    if (isThailandReservation || isDubaiReservation || requiredIdOrPassport) {
      if (hasBiomatchForGuestLeader) {
        return {
          steps: 5,
          activeStep: 4,
        };
      }

      if (isContractEnabled && !hasBiomatchForGuestLeader) {
        return {
          steps: 4,
          activeStep: 3,
        };
      }

      return {
        steps: 4,
        activeStep: 3,
      };
    }
  }

  if (
    !isContractEnabled &&
    COUNTRY_CODES_WITHOUT_SIGNATURE.includes(countryCode) &&
    !isThailandReservation
  ) {
    if (hasBiomatchForGuestLeader) {
      if (requiredIdOrPassport) {
        return {
          steps: 4,
          activeStep: 3,
        };
      }
    }

    if (requiredIdOrPassport) {
      return {
        steps: 3,
        activeStep: 2,
      };
    }
  }

  if (hasBiomatchForGuestLeader) {
    if (requiredIdOrPassport) {
      return {
        steps: 5,
        activeStep: 3,
      };
    }

    return {
      steps: 4,
      activeStep: 3,
    };
  }

  if (requiredIdOrPassport || hasBiomatchForGuestLeader) {
    return {
      steps: 4,
      activeStep: 4,
    };
  }

  return {
    steps: 3,
    activeStep: 3,
  };
}

type LocationTypes = {
  formData: any;
  number_of_guests?: number;
  children?: number;
  ocrWasUsed?: boolean;
  biomatch?: {
    reservation_id: string;
    biomatch_passed: boolean;
    biomatch_doc: string;
    biomatch_selfie: string;
  };
};

function DocScanScreen() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation<LocationTypes>();
  const isMounted = useIsMounted();
  const registerGuest = useRegisterGuest();
  const {ErrorModal, displayError, closeErrorModal} = useErrorModal();
  const {patchReservation, refreshReservation} = useReservation();
  const {hasGuestMembers, ...reservationDetails} = useComputedReservationDetails();
  const timeoutRef = React.useRef<any>();
  const webcamRef = React.useRef<WebcamRefTypes>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);
  const [isCaptureButtonDisabled, setIsCaptureButtonDisabled] = React.useState(true);
  const [isLoadingModalOpen, setIsLoadingModalOpen] = React.useState(false);

  const goBack = React.useCallback(() => {
    history.push('/form/add', location.state);
  }, [history, location.state]);

  React.useEffect(() => {
    const hasRequiredPreviousData = location.state?.formData;
    if (!hasRequiredPreviousData) {
      goBack();
    }
  }, [goBack, location.state]);

  React.useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const enableScreenshotButton = () => {
    if (isMounted.current) {
      setIsCaptureButtonDisabled(false);
    }
  };

  const getReservationPayload = () => {
    if (hasGuestMembers) {
      return null;
    }

    if (location.state?.children) {
      const computedNumberOfGuests =
        Number(location.state.number_of_guests) - Number(location.state.children);
      return {
        guest_group: {
          number_of_guests: computedNumberOfGuests > 0 ? computedNumberOfGuests : 0,
        },
      };
    }

    return {
      guest_group: {
        number_of_guests: location.state.number_of_guests,
      },
    };
  };

  const updateReservation = () => {
    const payload = getReservationPayload();
    if (payload) {
      return patchReservation(payload);
    }
    return {
      error: null,
      data: null,
    };
  };

  const finishCheckin = (registeredGuestName: string) => {
    const {url, state} = getFinishCheckinHistoryArgs({
      reservationDetails: {hasGuestMembers, ...reservationDetails},
      locationState: {
        registeredGuestName,
      },
    });
    history.push(url, state);
  };

  const registerGuestAndUpdateReservation = async (image = '') => {
    closeErrorModal();
    setIsLoadingModalOpen(true);

    const {error: reservationError} = await updateReservation();
    if (!reservationError && isMounted.current) {
      const {error: guestError, data: guest} = await registerGuest({
        guestData: {
          front_side_scan: image,
        },
      });

      if (!guestError && isMounted.current) {
        const {error: refreshError} = await refreshReservation();

        if (!refreshError && isMounted.current) {
          setIsLoadingModalOpen(false);
          finishCheckin(guest.full_name);
        } else {
          displayError(refreshError);
        }
      } else {
        displayError(guestError);
      }
    } else {
      displayError(reservationError);
    }

    if (isMounted.current) {
      setIsLoadingModalOpen(false);
    }
  };

  const getIsSignatureRequired = () => {
    const housingCountry = getHousingCountryCode(reservationDetails.reservation);

    if (COUNTRY_CODES_WITHOUT_SIGNATURE.includes(housingCountry)) {
      return false;
    }

    return reservationDetails.isDubaiReservation ||
      reservationDetails.isThailandReservation
      ? reservationDetails.isContractEnabled && !hasGuestMembers
      : true;
  };

  const goNextOrRegisterGuest = (image: string) => {
    const isSignatureRequired = getIsSignatureRequired();

    if (isSignatureRequired) {
      history.push('/form/add/sign', {
        ...location.state,
        frontSideScan: image,
      });
    } else {
      registerGuestAndUpdateReservation(image);
    }
  };

  const submitAfterTimeout = (image = '') => {
    timeoutRef.current = setTimeout(() => {
      if (isMounted.current) {
        setIsSuccessModalOpen(false);
        goNextOrRegisterGuest(image);
      }
    }, TIMEOUT_BEFORE_REDIRECT_MS);
  };

  const captureScreenshot = () => {
    try {
      const image = webcamRef?.current?.getScreenshot() || '';
      setIsSuccessModalOpen(true);
      submitAfterTimeout(image);
    } catch (err) {
      displayError(err);
    }
  };

  return (
    <>
      <ErrorModal />
      <Modal
        open={isLoadingModalOpen}
        title={t('sending_your_data')}
        text={t('it_could_take_seconds')}
        iconSrc={userIcon}
        iconAlt="Person"
      />
      <Modal
        open={isSuccessModalOpen}
        title={`${t('success')}!`}
        iconSrc={successIcon}
        iconAlt="Success"
      />
      <Header
        hideBackButton={isLoadingModalOpen}
        title={t('scan_document')}
        onBack={goBack}
        {...getHeaderProps({hasGuestMembers, ...reservationDetails})}
      />
      <DimensionsWrapper>
        <Content>
          <Title>
            {t('single_page_that_contains_all_the_data')}
            <br />
            {t('where_the_photo_is')}
          </Title>
          <Hint>{t('keep_your_doc_inside_the_box')}</Hint>
          <Webcam ref={webcamRef} onUserMedia={enableScreenshotButton} />
          <ButtonWrapper>
            <Button
              data-testid="submit-btn"
              label={t('capture')}
              onClick={captureScreenshot}
              disabled={isCaptureButtonDisabled}
              icon={<ButtonIcon src={cameraIcon} alt="Camera" />}
            />
          </ButtonWrapper>
        </Content>
      </DimensionsWrapper>
    </>
  );
}

export {DocScanScreen, getHeaderProps};
